import { useCallback, useMemo, useState } from 'react';
import { ButtonGroup } from '@antimatterhq/ui/components/buttonGroup';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Button } from '@antimatterhq/ui/components/button';
import { DatePicker } from '@antimatterhq/ui/components/datePicker';
import { CalendarDate, type DateValue } from '@internationalized/date';
import {
  dateFromCalendarDate,
  calendarDateFromDate,
} from '@antimatterhq/ui/utils/dateUtils';
import Table from '@antimatterhq/ui/components/table';
import { useVirtualServerTablePagination } from '@antimatterhq/ui/hooks/table/server/useVirtualServerTablePagination';
import { useTableColumns } from '@antimatterhq/ui/hooks/table/useTableColumns';
import {
  dbui_v1_OperationalAlert,
  dbui_v1_OperationalAlertList,
} from '@antimatterhq/asl-api-ts-v1';
import { useTableRowSelection } from '@antimatterhq/ui/hooks/table/useTableRowSelection';
import { useVirtuosoTableRows } from '@antimatterhq/ui/hooks/table/useVirtuosoTableRows';
import { useDefaultHandleEndReached } from '@antimatterhq/ui/hooks/table/useDefaultHandleEndReached';
import { Badge } from '@antimatterhq/ui/components/badge';
import { formatDate } from '@antimatterhq/ui/utils/format';
import { useSyncStateToQueryParam } from '@antimatterhq/ui/hooks/state/useSyncStateToQueryParam';
import { destr } from 'destr';
import { AlertData } from './AlertData';
import { useDismissAlerts } from '@/hooks/dbui/useDismissAlerts';
import {
  setDismissAlertQueryData,
  useGetAlertsInfinite,
  useOptimisticUpdateAlertsInfinite,
} from '@/hooks/dbui/useGetAlertsInfinite';
import { healthTableColumnDefinitions } from '@/components/table/HealthTable/healthTableColumnDefinitions';
import { useNavigateWithPreviousRouteQueryParam } from '@/hooks/nagivation/useNavigateWithPreviousRouteQueryParam';
import { getAlertBadgeColorVariant } from '@/lib/alerts/getAlertBadgeColorVariant';
import { getAlertStatusText } from '@/lib/alerts/getAlertStatusText';

const transformAlertData = (alert: dbui_v1_OperationalAlert): AlertData => ({
  id: alert.id || '',
  eventTime: alert.eventTime || '',
  resourceType: alert.resourceType || '',
  resourceName: alert.resourceName || '',
  duplicates: alert.duplicates || undefined,
  summary: alert.summary || '',
  details: alert.details || '',
  state: alert.state || null,
});

const StatusBadge = ({
  state,
}: {
  state: dbui_v1_OperationalAlert['state'];
}) => {
  return (
    <Badge
      variant="filled"
      color={getAlertBadgeColorVariant(state)}
      data-testid={`status-badge-${state || 'null'}`}
    >
      {getAlertStatusText(state)}
    </Badge>
  );
};

const getItemsFromPage = (page: dbui_v1_OperationalAlertList) =>
  page.alerts.map(transformAlertData);

function HealthTableContent() {
  const { mutateAsync: dismissAsync } = useDismissAlerts();
  const optimisticUpdate = useOptimisticUpdateAlertsInfinite();
  const navigate = useNavigateWithPreviousRouteQueryParam();
  const [isDismissing, setIsDismissing] = useState(false);

  const [includeNotOpen, setIncludeNotOpen] = useState<boolean>(
    destr<boolean>(
      new URLSearchParams(window.location.search).get('includeNotOpen'),
    ) ?? false,
  );

  const [date, setDate] = useState<Date | undefined>(
    destr<string | undefined>(
      new URLSearchParams(window.location.search).get('date'),
    )
      ? new Date(
          destr<string>(
            new URLSearchParams(window.location.search).get('date'),
          ),
        )
      : undefined,
  );
  const beforeDate = date && date.toISOString();

  useSyncStateToQueryParam({
    includeNotOpen,
    date,
  });

  const queryParams = useMemo(
    () => ({ includeNotOpen, beforeDate }),
    [includeNotOpen, beforeDate],
  );

  // Table hooks setup
  const { items, isLoading, hasNextPage, fetchNextPage, refetch, error } =
    useVirtualServerTablePagination({
      queryFn: useGetAlertsInfinite,
      baseQueryParams: queryParams,
      getItemsFromPage,
    });

  const dismissRows = useCallback(
    async (idToDismiss: string | string[]) => {
      setIsDismissing(true);
      await dismissAsync(
        { ids: Array.isArray(idToDismiss) ? idToDismiss : [idToDismiss] },
        {
          toasts: {
            success: 'Alerts dismissed',
            error: 'Failed to dismiss alert',
          },
          onSuccess: async () => {
            optimisticUpdate(
              (prev) => setDismissAlertQueryData(prev, idToDismiss),
              { ...queryParams },
            );
            setIsDismissing(false);
          },
          onError: async () => {
            setIsDismissing(false);
          },
        },
      );
    },
    [dismissAsync, optimisticUpdate, queryParams],
  );

  const {
    selectedRows,
    onToggleSelectAll,
    isSelected,
    isAllSelected,
    onToggleSelect,
  } = useTableRowSelection(items);

  const {
    columns,
    columnWidths,
    columnVisibility,
    pinnedColumns,
    columnOrders,
    toggleColumnVisibility,
    resetAllColumns,
    onColumnOrderChange,
  } = useTableColumns();

  const { fixedHeaderContent, itemContent } = useVirtuosoTableRows<AlertData>({
    columns,
    customBodyCells: useMemo(
      () => ({
        state: ({ item }) => <StatusBadge state={item.state} />,
        eventTime: ({ item }) => formatDate(item.eventTime),
        duplicates: ({ item }) => item.duplicates || '-',
        edit: ({ item }) => (
          <Table.ActionsCell
            data-testid={item.id}
            actions={
              item.state !== 'dismissed'
                ? [
                    {
                      label: 'Dismiss',
                      icon: 'lucide:circle-x',
                      onClick: () => dismissRows(item.id),
                    },
                    {
                      label: 'View full details',
                      icon: 'lucide:list',
                      onClick: () =>
                        navigate(`/explore/health/view/${item.id}`),
                    },
                  ]
                : [
                    {
                      label: 'View full details',
                      icon: 'lucide:list',
                      onClick: () =>
                        navigate(`/explore/health/view/${item.id}`),
                    },
                  ]
            }
          />
        ),
      }),
      [dismissRows, navigate],
    ),
    selection: useMemo(
      () => ({
        selectedRows,
        onToggleSelectAll,
        isSelected,
        isAllSelected,
        onToggleSelect,
      }),
      [
        selectedRows,
        onToggleSelectAll,
        isSelected,
        isAllSelected,
        onToggleSelect,
      ],
    ),
  });

  const handleEndReached = useDefaultHandleEndReached(
    fetchNextPage,
    isLoading,
    hasNextPage,
  );

  // Handle filter changes
  const toggleShowAll = useCallback(
    (value: string | undefined) => {
      setIncludeNotOpen(value === 'all');
      onToggleSelectAll(false);
      refetch();
    },
    [setIncludeNotOpen, onToggleSelectAll, refetch],
  );

  // Convert date to CalendarDate once
  const calendarDateValue = useMemo(() => {
    return date ? calendarDateFromDate(date) : null;
  }, [date]);

  // Handle date selection
  const onDateSelected = useCallback(
    (value: DateValue | null) => {
      if (value instanceof CalendarDate) {
        setDate(dateFromCalendarDate(value));
      } else if (value === null) {
        setDate(undefined);
      }
      onToggleSelectAll(false);
      refetch();
    },
    [setDate, onToggleSelectAll, refetch],
  );

  return (
    <Table.VirtualizedPageContainer>
      <div className="flex items-center justify-end gap-2">
        <div className="mr-auto flex w-full items-center gap-2">
          <DatePicker
            value={calendarDateValue}
            onChange={onDateSelected}
            placeholder="Before date"
            disabled={isLoading}
          />
          <ButtonGroup
            options={[
              { label: 'View all', value: 'all' },
              { label: 'View open only', value: 'open' },
            ]}
            onChange={toggleShowAll}
            value={includeNotOpen ? 'all' : 'open'}
            disabled={isLoading}
          />
          <Button
            disabled={selectedRows.length === 0}
            variant="default"
            onClick={() => dismissRows(selectedRows)}
            leftIcon="lucide:circle-x"
            loading={isDismissing}
          >
            Dismiss selected
          </Button>
        </div>

        <Table.ColumnManager
          pinnedColumns={pinnedColumns}
          columnOrders={columnOrders}
          columnVisibility={columnVisibility}
          toggleColumnVisibility={toggleColumnVisibility}
          resetColumns={resetAllColumns}
          onColumnOrderChange={onColumnOrderChange}
          data-testid="notables-column-manager"
        />
      </div>

      <Spacer size="lg" />

      <Table.StyleProvider
        virtualized={true}
        pageSize={items.length}
        columnWidths={columnWidths}
        columnVisibility={columnVisibility}
        data-testid="health-table-style-provider"
      >
        <Table.Virtual
          columns={columns}
          data={items}
          error={error}
          refetch={refetch}
          itemContent={itemContent}
          fixedHeaderContent={fixedHeaderContent}
          handleEndReached={handleEndReached}
          isLoading={isLoading}
          overscan={10}
          emptyMessage="No alerts found"
        />
      </Table.StyleProvider>
    </Table.VirtualizedPageContainer>
  );
}

export function HealthTable() {
  return (
    <Table.Provider
      columnDefinitions={healthTableColumnDefinitions}
      localStorageKeyPrefix="health-table"
    >
      <HealthTableContent />
    </Table.Provider>
  );
}
